import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightToBracket, faCircleCheck, faArrowRight, faHourglass } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useJoinContest } from '../../hooks/queries/useContests';
import { useUIStore } from '../../stores/uiStore';

const joinSchema = z.object({
  code: z
    .string()
    .min(1, 'Contest code is required')
    .regex(/^[a-zA-Z0-9]+$/, 'Code must be alphanumeric'),
});

type JoinFormData = z.infer<typeof joinSchema>;

export default function JoinContestModal() {
  const { activeModal, closeModal } = useUIStore();
  const isOpen = activeModal === 'join-contest';
  const navigate = useNavigate();
  const joinContest = useJoinContest();

  const [joinResult, setJoinResult] = useState<{ contestId: string; status: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<JoinFormData>({
    resolver: zodResolver(joinSchema),
    defaultValues: { code: '' },
  });

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      reset({ code: '' });
      setJoinResult(null);
    }
  }, [isOpen, reset]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); },
    [closeModal]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const onSubmit = async (data: JoinFormData) => {
    try {
      const result = await joinContest.mutateAsync({
        contestId: data.code,
        code: data.code,
      });
      const contestId = result?.contestId;
      if (contestId) {
        setJoinResult({ contestId, status: result?.status || 'pending' });
      } else {
        // Fallback — show success without navigation
        toast.success('Join request sent!');
        closeModal();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to join contest');
    }
  };

  const handleGoToContest = () => {
    if (joinResult?.contestId) {
      closeModal();
      navigate(`/contest/${joinResult.contestId}`);
    }
  };

  const handleGoToContests = () => {
    closeModal();
    navigate('/my-contests');
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={closeModal} />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-sm mx-4 bg-[#1A1D23] border border-[#2E3340] rounded-2xl shadow-2xl shadow-black/40 animate-scale-in overflow-hidden">
        {joinResult ? (
          /* ── Success State ── */
          <div className="px-6 py-8 text-center">
            {joinResult.status === 'accepted' ? (
              <>
                <div className="w-16 h-16 rounded-full bg-[#13EC5B]/10 flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon={faCircleCheck} className="text-[#13EC5B] text-2xl" />
                </div>
                <h2 className="text-lg font-bold text-white mb-2">You're In!</h2>
                <p className="text-sm text-gray-400 mb-6">
                  You've been added to the contest. Start building your team!
                </p>
                <div className="space-y-2">
                  <Button fullWidth onClick={handleGoToContest} rightIcon={<FontAwesomeIcon icon={faArrowRight} />}>
                    View Contest
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon={faHourglass} className="text-amber-400 text-2xl" />
                </div>
                <h2 className="text-lg font-bold text-white mb-2">Waiting for Approval</h2>
                <p className="text-sm text-gray-400 mb-6">
                  Your request has been sent to the contest owner. You'll be able to access the contest once they approve your request.
                </p>
                <Button fullWidth variant="secondary" onClick={closeModal}>
                  Got it
                </Button>
              </>
            )}
          </div>
        ) : (
          /* ── Join Form ── */
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h2 className="text-lg font-bold text-white">Join Contest</h2>
              <button
                onClick={closeModal}
                className="h-8 w-8 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="px-6 text-sm text-gray-400 mb-4">
              Enter the contest code shared by your friend to join their league.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="px-6 pb-6 space-y-4">
              <Input
                label="Contest Code"
                placeholder="e.g. ABC123"
                error={errors.code?.message}
                fullWidth
                autoFocus
                {...register('code')}
              />

              <Button
                type="submit"
                fullWidth
                loading={joinContest.isPending}
                leftIcon={<FontAwesomeIcon icon={faRightToBracket} />}
                className="bg-[#13EC5B] text-black font-bold"
              >
                Join Contest
              </Button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
